/**
 * The closed vocabulary of preference codes, and the resolved shape everything downstream reads.
 *
 * The codes are not invented here and are not guesses. They mirror the enums profile-api serialises
 * into `effectiveUserPreferences`:
 *
 * - `CustomMeasurements.java` — one `Unit` enum per measurement kind, plus `UnitSystem`
 * - `Formatting.java` — `FormattingStyle` per format family
 * - `AddressFormatting.java`, `FirstDayOfWeek.java`
 *
 * The OpenAPI spec types every one of them as a bare `string`, so nothing upstream of this file
 * constrains them. Closing the unions here is what turns a typo into a compile error instead of a
 * silently wrong unit.
 *
 * Product locales / operating countries / billing currencies live in `regional.ts`.
 */

import type { CurrencyCode } from './regional';

/**
 * Unit codes by measurement kind.
 *
 * The keys match the wire field names exactly, which is what lets the resolver walk both objects
 * with one loop rather than nine hand-written branches.
 */
export const UNIT_CODES = {
  distance: ['KM', 'MILES'],
  speed: ['KMH', 'MPH', 'MPS'],
  capacity: ['LITER', 'GAL_US', 'GAL_UK'],
  fuelConsumption: ['L_PER_100KM', 'MPG_US', 'MPG_UK'],
  electricConsumption: ['KWH_PER_100KM', 'MI_PER_KWH'],
  energy: ['KWH'],
  pressure: ['KPA', 'PSI'],
  temperature: ['C', 'F'],
  emission: ['GR_PER_KM', 'GR_PER_MI', 'LB_PER_MI'],
  /**
   * Payload weight. Named only by the library's `units` map, never by the deprecated
   * `measurements` block, which has no mass field and will not grow one.
   */
  mass: ['KG', 'LB'],
} as const satisfies Record<string, readonly string[]>;

/** Style codes by format family. Keys match the wire field names. */
export const STYLE_CODES = {
  date: ['MDY', 'DMY', 'YMD'],
  time: ['12H', '24H'],
  dateTime: ['MDY_12H', 'MDY_24H', 'DMY_12H', 'DMY_24H'],
  currency: ['USD_STYLE', 'CAD_STYLE', 'MXN_STYLE'],
  phone: ['US', 'CA', 'MX'],
  number: ['INTEGER', 'DECIMAL_2', 'DECIMAL_3'],
  address: ['US_STANDARD', 'MX_STANDARD'],
  firstDayOfWeek: ['SUNDAY', 'MONDAY', 'SATURDAY'],
} as const satisfies Record<string, readonly string[]>;

/**
 * The three systems, named as `csp-commonlib-internationalization` names them.
 *
 * `IMPERIAL` is the United Kingdom hybrid — miles and mph, but litres, kilograms, Celsius and
 * imperial mpg. A United States reader is `US_CUSTOMARY`, never `IMPERIAL`.
 *
 * This package used to call the US bundle `IMPERIAL`, which is what the word means colloquially in
 * North America and what several endpoints still call it on the wire. Adopting the library's names
 * costs one translation in the outbound dialect and buys a vocabulary where the same string means
 * the same thing on both sides, rather than one word with two meanings in one codebase.
 */
export const UNIT_SYSTEMS = ['METRIC', 'US_CUSTOMARY', 'IMPERIAL'] as const;

export type MeasurementKind = keyof typeof UNIT_CODES;
export type FormatFamily = keyof typeof STYLE_CODES;

export type UnitCodeFor<K extends MeasurementKind> =
  (typeof UNIT_CODES)[K][number];
export type StyleCodeFor<F extends FormatFamily> =
  (typeof STYLE_CODES)[F][number];

export type UnitSystem = (typeof UNIT_SYSTEMS)[number];

export type DistanceUnit = UnitCodeFor<'distance'>;
export type SpeedUnit = UnitCodeFor<'speed'>;
export type CapacityUnit = UnitCodeFor<'capacity'>;
export type FuelConsumptionUnit = UnitCodeFor<'fuelConsumption'>;
export type ElectricConsumptionUnit = UnitCodeFor<'electricConsumption'>;
export type EnergyUnit = UnitCodeFor<'energy'>;
export type PressureUnit = UnitCodeFor<'pressure'>;
export type TemperatureUnit = UnitCodeFor<'temperature'>;
export type EmissionUnit = UnitCodeFor<'emission'>;
export type MassUnit = UnitCodeFor<'mass'>;

export type DateStyle = StyleCodeFor<'date'>;
export type TimeStyle = StyleCodeFor<'time'>;
export type DateTimeStyle = StyleCodeFor<'dateTime'>;
export type CurrencyStyle = StyleCodeFor<'currency'>;
export type PhoneStyle = StyleCodeFor<'phone'>;
export type NumberStyle = StyleCodeFor<'number'>;
export type AddressStyle = StyleCodeFor<'address'>;
export type FirstDayOfWeek = StyleCodeFor<'firstDayOfWeek'>;

/** Every measurement kind resolved to exactly one unit. No optionals: resolution always answers. */
export type ResolvedUnits = {
  readonly [K in MeasurementKind]: UnitCodeFor<K>;
};

export type ResolvedFormats = {
  readonly [F in FormatFamily]: StyleCodeFor<F>;
} & {
  /** Derived from `currency`, because the backend sends a style rather than an ISO code. */
  readonly currencyCode: CurrencyCode;
};

/**
 * Everything the resolver decides, minus the signature derived from it.
 *
 * Split out so `signature.ts` can key on `keyof PreferenceValues` and make an unsignatured field a
 * compile error rather than a silent memoization bug.
 */
export type PreferenceValues = {
  readonly locale: string;
  readonly timeZone: string;
  readonly unitSystem: UnitSystem;
  readonly units: ResolvedUnits;
  readonly formats: ResolvedFormats;
};

export type ResolvedPreferences = PreferenceValues & {
  /**
   * Short derived string identifying this exact preference set. The memo key for the whole system:
   * the provider's context value, the formatter bundle and the Intl cache all key on it.
   */
  readonly signature: string;
};

export const ISSUE_REASONS = {
  /** A code arrived that is not in our closed union. The backend may have shipped ahead of us. */
  UNRECOGNISED_CODE: 'UNRECOGNISED_CODE',
  /** A field arrived with something other than a string — `{}`, a number, an array. */
  MALFORMED_VALUE: 'MALFORMED_VALUE',
  /**
   * Per-field codes contradict the `unitSystem` they sit beside. Not an error: a user may override
   * individual units. Recorded because a *whole* bundle disagreeing usually means something else.
   */
  UNIT_SYSTEM_MISMATCH: 'UNIT_SYSTEM_MISMATCH',
  /** The backend has no defaults for this locale, so its answer was not derived from it. */
  BACKEND_HAS_NO_LOCALE_DEFAULTS: 'BACKEND_HAS_NO_LOCALE_DEFAULTS',
  /** A money amount was not a finite number. The formatted result is empty. */
  NON_FINITE: 'NON_FINITE',
} as const;

export type IssueReason = (typeof ISSUE_REASONS)[keyof typeof ISSUE_REASONS];

/**
 * Something the resolver had to work around.
 *
 * Returned rather than thrown. The backend can legitimately ship a code before the frontend
 * supports it, and a localization layer that throws takes the page down with it.
 */
export type PreferenceIssue = {
  /** Dotted path as it appears on the wire, e.g. `effectiveUserPreferences.measurements.distance.unitCode`. */
  readonly field: string;
  /** What arrived, stringified for logging. `undefined` when the problem is absence of context. */
  readonly received?: string;
  readonly reason: IssueReason;
  /** The code actually used instead. */
  readonly resolvedTo: string;
};
